import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { apiClient } from '../utils/apiClient'
import { ACTIVE_ORG_CHANGED_EVENT } from '../utils/activeOrganization'
import { GitHubRepository } from '../types'

interface RepositoriesContextType {
  repositories: GitHubRepository[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  hasRepositories: boolean
}

const RepositoriesContext = createContext<RepositoriesContextType | undefined>(undefined)

interface RepositoriesProviderProps {
  children: ReactNode
}

export const RepositoriesProvider: React.FC<RepositoriesProviderProps> = ({ children }) => {
  const [repositories, setRepositories] = useState<GitHubRepository[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const { user } = useAuth()

  const fetchRepositories = async (force = false) => {
    if (!user || (hasFetched && !force)) return

    try {
      setIsLoading(true)
      setError(null)

      const reposData = await apiClient.get<GitHubRepository[]>('/api/repositories/connected')
      const validRepos = Array.isArray(reposData) ? reposData : []

      setRepositories(validRepos)
      setHasFetched(true)
    } catch (err) {
      console.error('Failed to fetch repositories:', err)
      setError('Failed to load repositories')
      setRepositories([])
    } finally {
      setIsLoading(false)
    }
  }

  const refetch = async () => {
    await fetchRepositories(true)
  }

  useEffect(() => {
    if (user && !hasFetched) {
      fetchRepositories()
    } else if (!user) {
      setRepositories([])
      setHasFetched(false)
      setError(null)
    }
  }, [user, hasFetched])

  useEffect(() => {
    if (!user) return
    const onOrgChanged = () => {
      setRepositories([])
      setHasFetched(false)
      void fetchRepositories(true)
    }
    window.addEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
    return () => window.removeEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
  }, [user])

  const hasRepositories = repositories.length > 0

  const value: RepositoriesContextType = {
    repositories,
    isLoading,
    error,
    refetch,
    hasRepositories
  }

  return (
    <RepositoriesContext.Provider value={value}>
      {children}
    </RepositoriesContext.Provider>
  )
}

export const useRepositories = (): RepositoriesContextType => {
  const context = useContext(RepositoriesContext)
  if (context === undefined) {
    throw new Error('useRepositories must be used within a RepositoriesProvider')
  }
  return context
}
